import React from "react";

interface TdahScoreDisplayProps {
    scores: Record<string, any>;
    interpretations: Record<string, any>;
  }

const TdahScoreDisplay: React.FC<TdahScoreDisplayProps> = ({ scores }) => {
  // Helper function to get severity level description based on category
  const getSeverityLevel = (score: number, category: string): string => {
    if (category === "regulacion_emocional") {
      if (score <= 5) return "Sin indicadores significativos";
      if (score <= 10) return "Indicadores leves";
      if (score <= 15) return "Indicadores moderados";
      return "Indicadores significativos";
    } else {
      // For inatencion and hiperactividad_impulsividad
      if (score <= 3) return "Sin indicadores significativos";
      if (score <= 6) return "Indicadores leves";
      if (score <= 9) return "Indicadores moderados";
      return "Indicadores significativos";
    }
  };

  // Helper function to get color based on score and category
  const getScoreColor = (score: number, category: string): string => {
    if (category === "regulacion_emocional") {
      if (score <= 5) return "text-green-500";
      if (score <= 10) return "text-blue-500";
      if (score <= 15) return "text-yellow-500";
      return "text-red-500";
    } else {
      if (score <= 3) return "text-green-500";
      if (score <= 6) return "text-blue-500";
      if (score <= 9) return "text-yellow-500";
      return "text-red-500";
    }
  };

  // Helper function to get background color based on score and category
  const getBarColor = (score: number, category: string): string => {
    if (category === "regulacion_emocional") {
      if (score <= 5) return "bg-green-500";
      if (score <= 10) return "bg-blue-500";
      if (score <= 15) return "bg-yellow-500";
      return "bg-red-500";
    } else {
      if (score <= 3) return "bg-green-500";
      if (score <= 6) return "bg-blue-500";
      if (score <= 9) return "bg-yellow-500";
      return "bg-red-500";
    }
  };

  // Helper function to get bar width as percentage
  const getBarWidth = (score: number, category: string): string => {
    const maxScore = category === "regulacion_emocional" ? 20 : 12;
    const percentage = (score / maxScore) * 100;
    return `${Math.max(5, Math.min(100, percentage))}%`;
  };

  // Helper function to get max scale value based on category
  const getMaxScale = (category: string): number => {
    return category === "regulacion_emocional" ? 20 : 12;
  };

  const renderScoreSection = (
    title: string,
    scores: Record<string, number>
  ) => (
    <div className="mb-6 rounded-xl bg-white p-5 shadow-md dark:bg-navy-800">
      <h5 className="mb-4 text-lg font-bold text-navy-700 dark:text-white">
        {title}
      </h5>
      <div className="space-y-4">
        {Object.entries(scores).map(([key, score]) => {
          const label = {
            inatencion: "Inatención",
            hiperactividad_impulsividad: "Hiperactividad/Impulsividad",
            regulacion_emocional: "Regulación Emocional",
          }[key];

          const maxScale = getMaxScale(key);

          return (
            <div key={key} className="w-full">
              <div className="mb-1 flex flex-wrap items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {label}
                </span>
                <div className="flex items-center">
                  <span className="mr-2 text-xs text-gray-500 dark:text-gray-400">
                    {score}/{maxScale}
                  </span>
                  <span
                    className={`text-sm font-medium ${getScoreColor(
                      score,
                      key
                    )}`}
                  >
                    {getSeverityLevel(score, key)}
                  </span>
                </div>
              </div>

              <div className="mb-2 h-2.5 w-full rounded-full bg-gray-200 dark:bg-navy-700">
                <div
                  className={`h-2.5 rounded-full ${getBarColor(score, key)}`}
                  style={{ width: getBarWidth(score, key) }}
                ></div>
              </div>

              <div className="flex justify-between px-1 text-xs text-gray-500">
                <span>0</span>
                <span>{maxScale / 2}</span>
                <span>{maxScale}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Summary section */}
      <div className="mb-6 rounded-xl bg-white p-5 shadow-md dark:bg-navy-800">
        <h5 className="mb-2 text-lg font-bold text-navy-700 dark:text-white">
          TDAH (Trastorno por Déficit de Atención e Hiperactividad)
        </h5>
        <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
          Evaluación comparativa de síntomas en la infancia y la actualidad
        </p>
      </div>

      {/* Childhood scores */}
      {renderScoreSection("Síntomas en la Infancia", scores.infancia)}

      {/* Current scores */}
      {renderScoreSection("Síntomas Actuales", scores.actualidad)}

      {/* Legend */}
      <div className="rounded-xl bg-white p-5 shadow-md dark:bg-navy-800">
        <h5 className="text-md mb-3 font-bold text-navy-700 dark:text-white">
          Interpretación de Puntuaciones
        </h5>
        <div className="space-y-6">
          <div>
            <h6 className="mb-2 text-sm font-semibold">
              Inatención e Hiperactividad/Impulsividad
            </h6>
            <div className="space-y-2">
              <div className="flex items-center">
                <div className="mr-2 h-3 w-3 rounded-full bg-green-500"></div>
                <span className="text-sm">
                  <span className="font-medium">0 - 3:</span> Sin indicadores
                  significativos
                </span>
              </div>
              <div className="flex items-center">
                <div className="mr-2 h-3 w-3 rounded-full bg-blue-500"></div>
                <span className="text-sm">
                  <span className="font-medium">4 - 6:</span> Indicadores leves
                </span>
              </div>
              <div className="flex items-center">
                <div className="mr-2 h-3 w-3 rounded-full bg-yellow-500"></div>
                <span className="text-sm">
                  <span className="font-medium">7 - 9:</span> Indicadores
                  moderados
                </span>
              </div>
              <div className="flex items-center">
                <div className="mr-2 h-3 w-3 rounded-full bg-red-500"></div>
                <span className="text-sm">
                  <span className="font-medium">10 - 12:</span> Indicadores
                  significativos
                </span>
              </div>
            </div>
          </div>

          <div>
            <h6 className="mb-2 text-sm font-semibold">Regulación Emocional</h6>
            <div className="space-y-2">
              <div className="flex items-center">
                <div className="mr-2 h-3 w-3 rounded-full bg-green-500"></div>
                <span className="text-sm">
                  <span className="font-medium">0 - 5:</span> Sin indicadores
                  significativos
                </span>
              </div>
              <div className="flex items-center">
                <div className="mr-2 h-3 w-3 rounded-full bg-blue-500"></div>
                <span className="text-sm">
                  <span className="font-medium">6 - 10:</span> Indicadores leves
                </span>
              </div>
              <div className="flex items-center">
                <div className="mr-2 h-3 w-3 rounded-full bg-yellow-500"></div>
                <span className="text-sm">
                  <span className="font-medium">11 - 15:</span> Indicadores
                  moderados
                </span>
              </div>
              <div className="flex items-center">
                <div className="mr-2 h-3 w-3 rounded-full bg-red-500"></div>
                <span className="text-sm">
                  <span className="font-medium">16 - 20:</span> Indicadores
                  significativos
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 border-t border-gray-200 pt-4 dark:border-gray-700">
          <h6 className="mb-2 text-sm font-semibold">
            Acerca de las puntuaciones TDAH
          </h6>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Las puntuaciones evalúan tres dimensiones principales: Inatención,
            Hiperactividad/Impulsividad y Regulación Emocional. Las dos primeras
            se miden en una escala de 0-12, mientras que la Regulación Emocional
            se mide en una escala de 0-20. La evaluación compara los síntomas
            presentes en la infancia con los actuales para determinar la
            persistencia y evolución del trastorno.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TdahScoreDisplay;
